import { useCallback } from 'react'

import { EmptyState } from '@/components/ui/StateMessage'
import { formatMoney } from '@/lib/format'

import styles from './Table.module.css'

export interface NotableRowData {
  date: string
  dept: string
  acct: string
  title: string
  who: string
  amt: number
}

interface NotableTableProps {
  rows: NotableRowData[]
  showDept: boolean
  deptName: (code: string) => string
  onActivate: (row: NotableRowData) => void
}

function NotableRow({
  row,
  showDept,
  deptName,
  onActivate,
}: Omit<NotableTableProps, 'rows'> & { row: NotableRowData }) {
  const handleClick = useCallback(() => onActivate(row), [onActivate, row])
  return (
    <tr className={styles.clickable} onClick={handleClick}>
      <td className={`${styles.left} ${styles.date}`}>{row.date}</td>
      {showDept ? <td className={styles.left}>{deptName(row.dept)}</td> : null}
      <td className={`${styles.left} ${styles.title}`}>
        <button
          type="button"
          className={styles.cellButton}
          aria-label={`${row.acct} ${row.title}, ${row.who}: view account transactions`}
        >
          {row.acct} — {row.title}
        </button>
      </td>
      <td className={`${styles.left} ${styles.muted}`}>{row.who}</td>
      <td className="num">{formatMoney(row.amt, { cents: true })}</td>
    </tr>
  )
}

export function NotableTable({ rows, showDept, deptName, onActivate }: NotableTableProps) {
  if (rows.length === 0) return <EmptyState>No standout single transactions.</EmptyState>
  return (
    <table className={`${styles.table} ${styles.txn}`}>
      <thead>
        <tr>
          <th className={styles.left} scope="col">
            Date
          </th>
          {showDept ? (
            <th className={styles.left} scope="col">
              Dept
            </th>
          ) : null}
          <th className={styles.left} scope="col">
            Account
          </th>
          <th className={styles.left} scope="col">
            Vendor
          </th>
          <th scope="col">Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <NotableRow
            key={`${row.date}-${row.acct}-${row.who}-${i}`}
            row={row}
            showDept={showDept}
            deptName={deptName}
            onActivate={onActivate}
          />
        ))}
      </tbody>
    </table>
  )
}
